import { useState, useMemo, useEffect } from "react"
import { Layout } from "../components/Layout"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { useGames } from "@/hooks/useGames"
import type { AdminGame } from "@/hooks/useGames"
import config from "@/constants/config"
import {
  VideoCameraIcon,
  FilmIcon,
  CalendarIcon,
  UserIcon,
  UserGroupIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

export function Games() {
  const [page, setPage] = useState(0)
  const [pageSize] = useState(50)
  // What's in the search box right now (every keystroke) vs. the term we actually
  // query with (debounced) — so we don't fire a backend request per keystroke.
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(id)
  }, [search])

  // A new search starts from page 1; otherwise the offset could point past the
  // (usually smaller) filtered result set and show an empty page.
  useEffect(() => {
    setPage(0)
  }, [debouncedSearch])

  const { data, isLoading, isPlaceholderData, error } = useGames({
    page: page + 1,
    pageSize,
    search: debouncedSearch,
  })

  const handleGameClick = (gameId: string) => {
    window.location.href = `/games/${gameId}`
  }

  const columns = useMemo<ColumnDef<AdminGame>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Game",
        cell: ({ row }) => (
          <div className="flex items-center">
            <VideoCameraIcon className="h-5 w-5 text-secondary mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <span className="font-medium text-primary underline-offset-2 hover:underline cursor-pointer block truncate">
                {row.original.title || "Untitled Game"}
              </span>
              <span className="text-xs text-secondary">{row.original.type}</span>
            </div>
          </div>
        ),
      },
      {
        id: "open_game",
        header: "",
        cell: ({ row }) => (
          <a
            href={`${config.frontendUrl}/view-game/${encodeURIComponent(row.original.public_id)}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary hover:underline"
          >
            Open game
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </a>
        ),
      },
      {
        accessorKey: "creator_name",
        header: "Creator",
        cell: ({ row }) => (
          <div className="flex items-center">
            <UserIcon className="h-4 w-4 text-secondary mr-1 flex-shrink-0" />
            <div className="min-w-0">
              <span className="text-sm block truncate">{row.original.creator_name || "Unknown"}</span>
              <span className="text-xs text-secondary block truncate">
                {row.original.creator_email}
              </span>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "team_name",
        header: "Team",
        cell: ({ row }) => (
          <div className="flex items-center">
            <UserGroupIcon className="h-4 w-4 text-secondary mr-1 flex-shrink-0" />
            <span className="text-sm">{row.original.team_name || "No team"}</span>
          </div>
        ),
      },
      {
        accessorKey: "when",
        header: "Game Date",
        cell: ({ row }) => (
          <div className="flex items-center">
            <CalendarIcon className="h-4 w-4 text-secondary mr-1" />
            <span className="text-sm text-secondary">
              {new Date(row.original.when).toLocaleDateString()}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "clips_count",
        header: "Clips",
        cell: ({ row }) => (
          <div className="flex items-center">
            <FilmIcon className="h-4 w-4 text-secondary mr-1" />
            <span className="text-sm font-medium">{row.original.clips_count}</span>
          </div>
        ),
      },
      {
        accessorKey: "total_size",
        header: "Size",
        cell: ({ row }) => (
          <span className="text-sm text-secondary">
            {formatBytes(row.original.total_size)}
          </span>
        ),
      },
      {
        accessorKey: "tagged_count",
        header: "Tagged",
        cell: ({ row }) => {
          const { tagged_count, clips_count } = row.original
          const allTagged = clips_count > 0 && tagged_count === clips_count
          return (
            <span
              className={`text-sm font-medium ${
                allTagged
                  ? "text-green-600"
                  : tagged_count > 0
                    ? "text-yellow-600"
                    : "text-secondary"
              }`}
            >
              {tagged_count}/{clips_count}
            </span>
          )
        },
      },
      {
        accessorKey: "predicted_count",
        header: "Predicted",
        cell: ({ row }) => {
          const { predicted_count, clips_count } = row.original
          const allPredicted = clips_count > 0 && predicted_count === clips_count
          return (
            <span
              className={`text-sm font-medium ${
                allPredicted
                  ? "text-green-600"
                  : predicted_count > 0
                    ? "text-yellow-600"
                    : "text-secondary"
              }`}
            >
              {predicted_count}/{clips_count}
            </span>
          )
        },
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-sm text-secondary">
            {new Date(row.original.created_at).toLocaleDateString()}
          </span>
        ),
      },
    ],
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Games</h1>
          <p className="text-secondary">
            View uploaded games, clip counts, and tagging status
          </p>
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : !data || (data.games.length === 0 && !debouncedSearch) ? (
          <EmptyState
            icon={<VideoCameraIcon className="h-12 w-12 text-secondary" />}
            title="No games found"
            description="No games have been uploaded yet."
          />
        ) : (
          <>
            {/* Server-side search: the box queries ALL games (title, team name,
                creator name/email, or an exact public game id), not just the rows
                loaded on the current page. */}
            <DataTable
              columns={columns}
              data={data.games}
              searchValue={search}
              onSearchChange={setSearch}
              // The server pages this table (footer below); DataTable's own pager
              // would slice each server page again, 10 rows at a time.
              showPagination={false}
              searchPlaceholder="Search by title, team, creator, or game ID…"
              onRowClick={(row) => handleGameClick(row.public_id)}
            />

            {data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-secondary">
                  Showing {(data.pagination.page - 1) * data.pagination.pageSize + 1}-{Math.min(data.pagination.page * data.pagination.pageSize, data.pagination.totalCount)} of{" "}
                  {data.pagination.totalCount} games
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    className="px-3 py-1 text-sm rounded border border-border hover:bg-surface disabled:opacity-50"
                    // Placeholder rows are the previous query's page; its bounds
                    // don't apply to the page being loaded.
                    disabled={isPlaceholderData || !data.pagination.hasPreviousPage}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-secondary">
                    Page {data.pagination.page} of {data.pagination.totalPages}
                  </span>
                  <button
                    className="px-3 py-1 text-sm rounded border border-border hover:bg-surface disabled:opacity-50"
                    disabled={isPlaceholderData || !data.pagination.hasNextPage}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  )
}
